import { useCallback, useEffect, useState } from 'react';
import type { CardView } from '../types';

interface UseCardViewArgs {
    cardId: string;
    expandedCardId?: string | null;
    onExpandChange?: (id: string | null) => void;
}

interface UseCardViewResult {
    view: CardView;
    showStats: () => void;
    showMap: () => void;
    showImage: () => void;
    toggleStats: () => void;
    toggleMap: () => void;
}

/**
 * Owns the per-card view-state machine: image | stats | map.
 * When the parent passes expandedCardId, only one card across the grid stays expanded;
 * any other card opening an overlay collapses this one back to image view.
 */
export const useCardView = ({
    cardId,
    expandedCardId,
    onExpandChange,
}: UseCardViewArgs): UseCardViewResult => {
    const [view, setView] = useState<CardView>('image');

    // collapse to image when another card claims expansion
    useEffect(() => {
        if (expandedCardId && expandedCardId !== cardId && view !== 'image') {
            setView('image');
        }
    }, [expandedCardId, cardId, view]);

    // Esc closes any open overlay back to image
    useEffect(() => {
        if (view === 'image') return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setView('image');
                onExpandChange?.(null);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [view, onExpandChange]);

    const setAndAnnounce = useCallback((next: CardView) => {
        setView(next);
        onExpandChange?.(next === 'image' ? null : cardId);
    }, [cardId, onExpandChange]);

    return {
        view,
        showStats: () => setAndAnnounce('stats'),
        showMap: () => setAndAnnounce('map'),
        showImage: () => setAndAnnounce('image'),
        toggleStats: () => setAndAnnounce(view === 'stats' ? 'image' : 'stats'),
        toggleMap: () => setAndAnnounce(view === 'map' ? 'image' : 'map'),
    };
};
